import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, KeyRound, Eye, EyeOff, Loader2, ExternalLink, FileSpreadsheet } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import CommentsPanel from "./CommentsPanel";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pwOpen, setPwOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/posts").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = async (id) => {
    await api.delete(`/posts/${id}`);
    toast({ title: "Grumble deleted" });
    load();
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const [downloading, setDownloading] = useState(false);
  const downloadCosts = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/task-costs/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Actual Task Costs.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: "Downloaded", description: "Actual Task Costs.xlsx" });
    } catch (e) {
      toast({ title: "Download failed", variant: "destructive" });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f10] text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="border-b border-[#26262a]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Admin</h1>
            <p className="text-xs text-gray-500">{user}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadCosts} disabled={downloading} className="border-[#2a2a2e] bg-transparent text-gray-200 hover:bg-[#1c1c1f] hover:text-white" title="Download Actual Task Costs spreadsheet">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />} Download Task Costs (.xlsx)
            </Button>
            <a href="/" target="_blank" rel="noreferrer"><Button variant="outline" className="border-[#2a2a2e] bg-transparent text-gray-200 hover:bg-[#1c1c1f] hover:text-white"><ExternalLink className="w-4 h-4 mr-1.5" /> View site</Button></a>
            <Button variant="outline" onClick={() => setPwOpen(true)} className="border-[#2a2a2e] bg-transparent text-gray-200 hover:bg-[#1c1c1f] hover:text-white"><KeyRound className="w-4 h-4 mr-1.5" /> Password</Button>
            <Button variant="outline" onClick={doLogout} className="border-[#2a2a2e] bg-transparent text-gray-200 hover:bg-[#1c1c1f] hover:text-white"><LogOut className="w-4 h-4 mr-1.5" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">Posts <span className="text-gray-500">({posts.length})</span></h2>
          <Link to="/admin/new"><Button className="bg-[#e63946] hover:bg-[#d12f3c] text-white"><Plus className="w-4 h-4 mr-1.5" /> New post</Button></Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-[#e63946]" /></div>
        ) : posts.length === 0 ? (
          <p className="text-gray-500 text-center py-20">No posts yet. Create your first grumble.</p>
        ) : (
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.id} className="flex items-center justify-between bg-[#161618] border border-[#26262a] rounded-lg px-5 py-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{p.title}</h3>
                    {p.published ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full"><Eye className="w-3 h-3" /> Live</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 bg-gray-400/10 px-2 py-0.5 rounded-full"><EyeOff className="w-3 h-3" /> Draft</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{p.subtitle}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Link to={`/admin/edit/${p.id}`}><Button size="sm" variant="outline" className="border-[#2a2a2e] bg-transparent text-gray-200 hover:bg-[#1c1c1f] hover:text-white"><Pencil className="w-4 h-4" /></Button></Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="border-[#2a2a2e] bg-transparent text-red-400 hover:bg-red-500/10 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this grumble?</AlertDialogTitle>
                        <AlertDialogDescription>"{p.title}" will be permanently removed. This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(p.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}

        <CommentsPanel />
      </main>

      <ChangePasswordDialog open={pwOpen} setOpen={setPwOpen} />
    </div>
  );
}

function ChangePasswordDialog({ open, setOpen }) {
  const { toast } = useToast();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast({ title: "Password updated" });
      setOpen(false);
      setOldPw(""); setNewPw("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="oldpw">Current password</Label>
            <Input id="oldpw" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="newpw">New password</Label>
            <Input id="newpw" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1.5" />
          </div>
          {err && <p className="text-sm text-red-500">{err}</p>}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-[#e63946] hover:bg-[#d12f3c] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
